import { Field, Formik } from "formik";
import moment from "moment";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { IoMdArrowBack } from "react-icons/io";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

import UTILS from "../../utils";
import Partials from "../partials";
import Fields from "../formFields";
import Blocks from "../blocks";
import { createVirtualClass, updateVirtualClass } from "../../api/virtualClass";
import GroupDiscussion from "../../assets/images/group-discussion.png";
import { subjectPromiseOptions } from "../../utils/subject";
import { repeatOptions } from "../../utils/repeatOptions";
import { createClassPlan } from "../../api/classPlan";
import { getClassDetailsById } from "../../utils/reactQueries";
import { getAllStudentsOfVirtualClass } from "../../api/students";

function getRoundedDate() {
  let minutes = 5;
  let ROUNDING = 1000 * 60 * minutes;
  let start = moment();
  return moment(Math.ceil(+start / ROUNDING) * ROUNDING);
}

const KeyboardDateTimeInput = ({ field, form, ...props }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState(
    moment(field.value).format("YYYY-MM-DD HH:mm")
  );
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(moment(field.value).format("YYYY-MM-DD HH:mm"));
  }, [field.value]);

  // Handle clicks outside the component
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        const isTimePickerElement = event.target.closest('.rdtTime') || 
                                  event.target.closest('.rdtCounter');
        if (!isTimePickerElement) {
          setShowCalendar(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    const parsedDate = moment(value, "YYYY-MM-DD HH:mm", true);
    if (parsedDate.isValid()) {
      form.setFieldValue(field.name, parsedDate.toISOString());
    }
  };

  const handleBlur = () => {
    const parsedDate = moment(inputValue, "YYYY-MM-DD HH:mm", true);
    if (!parsedDate.isValid()) {
      setInputValue(moment(field.value).format("YYYY-MM-DD HH:mm"));
    }
  };

  const setToCurrentTime = () => {
    const now = getRoundedDate();
    form.setFieldValue(field.name, now.toISOString());
    setInputValue(now.format("YYYY-MM-DD HH:mm"));
    setShowCalendar(false);
  };

  const handleCalendarChange = (momentObj) => {
    if (momentObj.isValid()) {
      form.setFieldValue(field.name, momentObj.toISOString());
      setInputValue(momentObj.format("YYYY-MM-DD HH:mm"));
    }
  };

  const handleCloseCalendar = () => {
    setShowCalendar(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className={`flex items-center gap-2 w-full ${
        form.errors[field.name] && form.touched[field.name] 
          ? 'border-red-500 border rounded-lg' 
          : ''
      }`}>
        <div className="flex items-center border rounded-lg flex-1">
          <span className="px-3">
            <i className={`text-xl icon icon-calendar`}></i>
          </span>
          <input
            type="text"
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={() => setShowCalendar(true)}
            className="flex-1 p-2 focus:outline-none"
            placeholder="YYYY-MM-DD HH:mm"
          />
        </div>
        <button
          type="button"
          onClick={setToCurrentTime}
          className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
          title="Set to current time"
        >
          Now
        </button>
      </div>
      
      {showCalendar && (
        <div className="absolute z-[100] mt-1 bg-white border rounded-lg shadow-lg">
          <Datetime
            input={false}
            value={moment(field.value)}
            onChange={handleCalendarChange}
            timeConstraints={{ minutes: { step: 5 } }}
            isValidDate={(current) => {
              return current.isSameOrAfter(moment().subtract(1, 'minute'), 'minute');
            }}
          />
          <div className="p-2 border-t flex justify-end">
            <button 
              onClick={handleCloseCalendar}
              className="px-3 py-1 bg-black text-white rounded hover:bg-black"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
KeyboardDateTimeInput.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]).isRequired,
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    errors: PropTypes.object,
    touched: PropTypes.object,
  }).isRequired,
};

const DropdownWrapper = ({ children, zIndex = 30, isDuration = false }) => {
  return (
    <div className="relative" style={{ zIndex: isDuration ? 50 : zIndex }}>
      {children}
    </div>
  );
};

DropdownWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  zIndex: PropTypes.number,
  isDuration: PropTypes.bool, // Add this new prop
};


const PageCreateVirtualClass = () => {
  const [initValues, setInitValues] = useState({
    date: getRoundedDate().toISOString(),
    title: "",
    duration_seconds: { label: "30 Mins", value: 30 * 60 },
    subject: "",
    repeats: "",
    description: "",
  });
  const [shouldRedirectAfterEdit, _setShouldRedirect] = useState(false);
  const [classDetails, setClassDetails] = useState({});
  const queryClient = useQueryClient();
  const params = useParams();
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isRedirect = searchParams.get("back");
  const classId = params.classId;
  let isEditing = !!classId;

  const getStudentDetails = (classId) => {
    return useQuery(
      ["getAllStudentsOfVirtualClass", classId],
      () => getAllStudentsOfVirtualClass(classId),
      {
        enabled: !!classId,
        staleTime: 180000,
        cacheTime: 600000,
      }
    );
  };

  const {
    status: isStatus,
    data: studentData,
    error: dataFetchingError,
    isFetching: fetchingStudentDetails,
  } = getStudentDetails(classId);
  const students = Array.isArray(studentData?.data?.body)
    ? studentData.data.body
    : [];

  useEffect(() => {
    localStorage.removeItem("classPlanID");
  }, []);

  const {
    status,
    data: classData,
    error: classError,
    isFetching,
  } = getClassDetailsById(classId);

  const fetchData = async () => {
    await queryClient.refetchQueries(["getAllVirtualClassesRTQ"]);
  };

  useEffect(() => {
  if (classData?.data) {
    let classdata = classData.data;
    setClassDetails(classdata);
    const duration = UTILS.virtualClass
      .durationOptions()
      .find((dur) => dur.value === classdata.duration_seconds);
    const start_time = moment
      .unix(classdata.start_time)
      .format("YYYY-MM-DD HH:mm");
    
    setInitValues({
      id: classdata.id,
      teacher_id: classdata.teacher_id,
      date: moment(start_time, "YYYY-MM-DD HH:mm").toISOString(),
      title: classdata.title,
      duration_seconds: duration,
      subject: {
        value: classdata?.subject?.title.toLowerCase(),
        label: classdata?.subject?.title,
        subject: {
          id: classdata?.subject?.id,
          title: classdata?.subject?.title,
          description: classdata?.subject?.description,
        },
      },
      repeats: {
        value: classdata?.repeats?.toLowerCase(),
        label: classdata?.repeats[0]?.toUpperCase() + classdata.repeats.slice(1),
      },
      description: classdata.description || "",
      start_url: classdata?.start_url,
      join_url: classdata?.join_url,
      student_count: classdata.student_count,
    });
  }
}, [classData]);

  const redirectToPage = (classId) => {
    if (isEditing) {
      if (shouldRedirectAfterEdit) {
        history.push(`/class/${classId}/edit-students`);
      } else if (isRedirect) {
        history.push(`/teacher/${classId}/create-class-plan`);
      } else {
        history.push("/teacher/start-live-class");
      }
    } else {
      localStorage.setItem("classId", classId);
      history.push(`/teacher/${classId}/create-class-plan`);
    }
  };

  const { isLoading, error, isError, mutate, data } = useMutation(
    createVirtualClass,
    {
      onSuccess: (data, variables) => {
        fetchData();
        localStorage.removeItem("classPlanID");
        if (data?.data?.body?.id) {
          redirectToPage(data?.data?.body?.id);
        }
      },
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const {
    isLoading: isUpdateLoading,
    error: updateError,
    isError: isUpdateError,
    mutate: updateMutate,
    data: updateData,
  } = useMutation(updateVirtualClass, {
    onSuccess: (data, variables) => {
      fetchData();
      if (data?.data?.body?.id) {
        redirectToPage(data.data.body.id);
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

 const createClass = async (values) => {
  const {
    id,
    teacher_id,
    title,
    description,
    repeats,
    duration_seconds,
    subject,
    date,
  } = values;

  const start_time = moment(date).unix();
  const end_time = start_time + duration_seconds.value;

  const payload = {
    teacher_id,
    start_time,
    end_time,
    title,
    description: description || "", // Ensure description is included
    repeats: repeats?.value || repeats, // Handle both object and string cases
    duration_seconds: duration_seconds.value,
  };

  if (isEditing) {
    payload.id = id;
    payload.subject = subject.subject || subject;
    return await updateMutate(payload);
  } else {
    payload.subject_id = subject.subject?.id || subject.value;
    return await mutate(payload);
  }
};

  const { mutate: createNewClassPlan } = useMutation(createClassPlan, {
    onSuccess: (data, variables) => {
      if (data.status === 201) {
        const classPlanID = data?.data?.body?.id;
        localStorage.setItem("classPlanID", classPlanID);
        if (classPlanID) {
          history.push(`/class/${classDetails?.id}/class-plan/${classPlanID}`);
        }
      }
    },
    onError: (err) => {
      console.log(err);
      alert("Something Went wrong...");
    },
  });

  const editClassPlan = () => {
    const classPlanID = classDetails?.classPlan?.id;
    if (classPlanID) {
      history.push(`/class/${classDetails?.id}/class-plan/${classPlanID}`);
      localStorage.setItem("classPlanID", classPlanID);
    } else {
      const payload = {
        id: classDetails?.id,
        title: classDetails?.title,
        description: "string",
        createdOn: 0,
        modifiedOn: 0,
        teacher_id: classDetails?.teacher_id,
      };
      createNewClassPlan({ classPlanData: payload, classID: classDetails?.id });
    }
  };

  const goToHome = () => {
    history.push("/");
  };
  const getVisibleTextLength = (html) => {
  if (!html) return 0;
  
  const cleanText = html.replace(/<[^>]*>/g, "").trim();
  return cleanText.length;
};

    return (
    <div className={"page PageCreateVirtualClass"}>
      <div className="container mx-auto px-4"> {/* Added px-4 for mobile padding */}
        <h3
          className={
            "flex items-center justify-center gap-2 md:hidden text-xl text-center pt-8 pb-4 font-['Poppins-Bold']" // Reduced pt-16 to pt-8
          }
        >
          <IoMdArrowBack
            size={20}
            className="cursor-pointer"
            onClick={() => goToHome()}
          />
          {isEditing ? "Edit your virtual class" : "Create your virtual class"}
        </h3>
        <div className="flex flex-col md:flex-row items-center">
          <div className="bgWrapper md:flex justify-center items-center bg-white">
            <img src={GroupDiscussion} alt="" draggable={false} className="max-w-full" />
          </div>
          <div className="pageContent w-full max-w-2xl mx-auto md:mr-0 md:ml-auto mb-4 bg-white p-4 rounded-xl md:max-[80vh]-screen md:overflow-auto">
            <h3 className="hidden md:flex text-4xl text-center mb-8 font-['Poppins-Bold'] items-center gap-2">
              <IoMdArrowBack
                size={40}
                className="cursor-pointer"
                onClick={() => goToHome()}
              />
              {isEditing
                ? "Edit your virtual class"
                : "Create your virtual class"}
            </h3>
            <Blocks.ErrorBoundary>
              <Formik
                initialValues={initValues}
                enableReinitialize
                validateOnBlur={true}
                validateOnChange={true}
                validate={(values) => {
                  const errors = {};
                  if (moment(values.date).isBefore(moment().subtract(1, 'minute'))) {
                    errors.date = 'Class Date and Time cannot be in the past';
                  }
                  return errors;
                }} 
                validationSchema={
                  isEditing
                    ? UTILS.forms.EditVirtualClassSchema
                    : UTILS.forms.CreateVirtualClassSchema
                }
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                  createClass(values);
                }}
              >
                {({
                  values,
                  handleSubmit,
                  setFieldValue,
                  errors,
                  touched,
                  submitForm,
                  isSubmitting,
                }) => {
                  return (
                    <form onSubmit={handleSubmit} className="pt-4 pb-6">
                      <div className={"mb-6 "}>
                        <h4>
                          UTC{" "}
                          {UTILS.time.utcLabel(
                            UTILS.time.getUserLocalTimeZone()
                          )}{" "}
                          {UTILS.time.getUserLocalTimeZone()}
                        </h4>
                      </div>
                      <Field
                        type="text"
                        name="title"
                        placeholder="Add Title"
                        iconBefore={
                          <i className={`text-xl icon icon-git-commit`}></i>
                        }
                        component={Fields.FieldInput}
                      />

                      <div className="flex flex-col sm:flex-row gap-4 w-full">
                        <div className="w-full"> {/* Full width on mobile */}
                          <Field
                            name="date"
                            component={KeyboardDateTimeInput}
                          />
                          {errors.date && touched.date && (
                            <div className="text-red-500 text-sm mt-1">{errors.date}</div>
                          )}
                        </div>

                        <div className="w-full relative z-50">
                          <Field
                            type="select"
                            name="duration_seconds"
                            placeholder="Duration"
                            iconBefore={<i className="text-xl icon icon-clock"></i>}
                            options={UTILS.virtualClass.durationOptions()}
                            wrapperClasses="w-full"
                            component={Fields.FieldSelect}
                          />
                        </div>
                      </div>

                      <DropdownWrapper zIndex={40}>
                        <Field
                          name="subject"
                          placeholder="Subject"
                          type="select"
                          iconBefore={<i className={`text-xl icon icon-menu`}></i>}
                          promiseOptions={subjectPromiseOptions}
                          component={Fields.FieldAsyncSelect}
                        />
                      </DropdownWrapper>

                      <DropdownWrapper zIndex={30}>
                        <Field
                          name="repeats"
                          type="select"
                          placeholder="Repeat"
                          iconBefore={<i className="icon icon-repeat text-xl"></i>}
                          promiseOptions={repeatOptions}
                          component={Fields.FieldAsyncSelect}
                        />
                      </DropdownWrapper>

                      <div className="relative z-15">
                        <Field
                          name={"description"}
                          iconBefore={<i className="icon icon-list text-xl"></i>}
                          placeholder="Description"
                          htmlValue={values["description"]}
                          component={Fields.FieldTextEditor}
                        />
                        <div className={`text-xs mt-1 text-right ${
                          getVisibleTextLength(values.description) > 900 ? 
                            (getVisibleTextLength(values.description) > 1000 ? 
                              "text-red-500" : "text-yellow-500") : 
                            "text-gray-500"
                        }`}>
                          {getVisibleTextLength(values.description)}/1000 characters
                          {errors.description && touched?.description && (
                            <div className="text-red-500 text-left mt-1">{errors.description}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-center items-center flex-wrap gap-5">
                          {Object.keys(errors).length > 0 && errors.date && (
                            <div className="w-full text-red-500 text-sm text-center mb-2">
                              {errors.date}
                            </div>
                          )}
                        <button
                          disabled={
                            isSubmitting || 
                            isLoading || 
                            Object.keys(errors).length > 0 ||
                            (values["description"] && 
                            values["description"].length > 1000)
                          }
                          className={`${
                            !isEditing && "w-full"
                          } bg-black hover:bg-slate-600 text-white font-bold py-2 px-8 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center ${
                            Object.keys(errors).length > 0 ||
                            (values["description"] && 
                            values["description"].length > 1000) ? 
                              "opacity-50 cursor-not-allowed" : ""
                          }`}
                          type="submit"
                        >
                          {isLoading && <Partials.PartialLoading />}
                          {isEditing ? "Save" : "Continue"}
                        </button>

                        {isEditing && (
                          <>
                            <span className="hidden lg:block px-4 py-2 text-sm text-center">
                              or
                            </span>
                            <div className="flex justify-start items-center gap-2 ">
                              {students?.length >= 0 && (
                                <>
                                  <span
                                    onClick={() => {
                                      _setShouldRedirect(true);
                                      submitForm();
                                    }}
                                    className="text-black text-lg underline cursor-pointer flex items-center gap-5"
                                  >
                                    Edit Student
                                    {isLoading && (
                                      <Partials.PartialLoading
                                        classes={"text-zinc-800 -h-10 -w-10"}
                                      />
                                    )}
                                  </span>
                                  /
                                </>
                              )}
                              <span
                                onClick={() => editClassPlan()}
                                className="text-black text-lg underline cursor-pointer"
                              >
                                update Class plan
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </Blocks.ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageCreateVirtualClass;